/**
 * The dispatcher.
 *
 * Every reducer sees every event and returns null for the ones it does not care about. That
 * looks wasteful and is deliberate: one event routinely has to move several things at once —
 * a sale touches stock, cost, cash and the ledger — and a first-match-wins dispatch would
 * quietly drop all but one of them.
 */

import type { AnyDomainEvent } from '@abastece/shared';
import { reduceUsuario } from './projections/usuarios.js';
import { reduceSesion } from './projections/sesiones.js';
import { reduceDispositivo } from './projections/dispositivos.js';
import { reduceConfig } from './projections/config.js';
import { reduceProducto } from './projections/productos.js';
import { reducePrecio } from './projections/precios.js';
import { reduceOferta } from './projections/ofertas.js';
import { reduceProveedor } from './projections/proveedores.js';
import { reduceCompra } from './projections/compras.js';
import { reduceCaja } from './projections/caja.js';
import { reduceVenta } from './projections/ventas.js';
import { reduceRecarga } from './projections/recargas.js';
import { reduceInventario } from './projections/inventario.js';
import { reduceCliente } from './projections/clientes.js';
import { reduceJornada } from './projections/reparto.js';
import { reduceGasto } from './projections/gastos.js';
import { reducePlanilla } from './projections/planilla.js';
import { reduceFiado } from './projections/fiado.js';
import { reduceDevolucion } from './projections/devoluciones.js';
import { reduceEfectivo } from './projections/efectivo.js';
import { reduceConteos } from './projections/conteos.js';
import { comprobarPermiso } from './permisos.js';
import type { ApplyResult, Reader } from './state.js';

type Reducer = (event: AnyDomainEvent, read: Reader) => Promise<ApplyResult | null>;

/** Register new reducers here as each phase lands. */
const REDUCERS: readonly Reducer[] = [
  reduceUsuario,
  reduceSesion,
  reduceDispositivo,
  reduceConfig,
  reduceProducto,
  reducePrecio,
  reduceOferta,
  reduceProveedor,
  reduceCompra,
  reduceCaja,
  reduceVenta,
  reduceRecarga,
  reduceInventario,
  reduceCliente,
  reduceJornada,
  reduceGasto,
  reducePlanilla,
  reduceFiado,
  reduceDevolucion,
  reduceEfectivo,
  reduceConteos,
];

/**
 * A reducer that throws — an entry that does not balance, a row it did not expect — is a bug, not
 * a decision about the event. Thrown out of here it stopped a sync: the server rolled back the
 * whole push and the phone sent the same push again for ever, so nothing it recorded after that
 * event ever arrived, and a phone pulling it failed the same way. Caught, the event is refused
 * like any other: kept in the log, listed for the owner to see, and everything after it goes on.
 * The same state throws the same way everywhere, so every device refuses it alike. `interno`
 * marks it, so the tests fail on it instead of passing it as a refusal.
 */
export async function applyEvent(event: AnyDomainEvent, read: Reader): Promise<ApplyResult> {
  try {
    return await aplicarEvento(event, read);
  } catch (e) {
    const mensaje = e instanceof Error ? e.message : String(e);
    console.error(`[applyEvent] ${event.type} ${event.id}:`, e);
    return { effects: [], rejections: [{ eventId: event.id, reason: `Error interno al aplicarlo: ${mensaje}`, interno: true }] };
  }
}

async function aplicarEvento(event: AnyDomainEvent, read: Reader): Promise<ApplyResult> {
  // Who may do it comes before what it does: the owner's events from anybody else change nothing.
  const sinPermiso = await comprobarPermiso(event, read);
  if (sinPermiso) return { effects: [], rejections: [sinPermiso] };

  const effects: ApplyResult['effects'] = [];
  const rejections: ApplyResult['rejections'] = [];
  let handled = false;

  for (const reduce of REDUCERS) {
    const result = await reduce(event, read);
    if (!result) continue;
    handled = true;
    effects.push(...result.effects);
    rejections.push(...result.rejections);
  }

  if (!handled) {
    rejections.push({ eventId: event.id, reason: `Sin reductor para el evento ${event.type}` });
  }

  // An entry with nothing in it (`entry`) books nothing, and is not kept.
  return { effects: effects.filter((e) => e.kind !== 'asiento' || e.entry.lineas.length > 0), rejections };
}

export async function applyEvents(
  events: readonly AnyDomainEvent[],
  read: Reader,
): Promise<ApplyResult> {
  const effects: ApplyResult['effects'] = [];
  const rejections: ApplyResult['rejections'] = [];
  for (const event of events) {
    const r = await applyEvent(event, read);
    effects.push(...r.effects);
    rejections.push(...r.rejections);
  }
  return { effects, rejections };
}
